import React, { Component, useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { ValidationError, ExpandingTextarea, HelpText } from '../../components';
import IconEye from '../IconEye/IconEye';
import IconEyeOff from '../IconEyeOff/IconEyeOff';

import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    labelClassName,
    customErrorText,
    helpText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    hidePasswordToggle,
    ...rest
  } = props;

  const intl = useIntl();
  const [passwordVisible, setPasswordVisible] = useState(false);

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';
  const isPasswordInput = input.type === 'password';
  const showPasswordToggle = isPasswordInput && !hidePasswordToggle;

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      // [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
      [css.inputPassword]: showPasswordToggle,
    });
  const maxLength = CONTENT_MAX_LENGTH;
  // Toggle the input type between 'password' and 'text' when the eye icon is used.
  const inputType = showPasswordToggle && passwordVisible ? 'text' : type;
  const inputProps = isTextarea
    ? {
        className: inputClasses,
        id,
        rows: 1,
        maxLength,
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
      }
    : isUncontrolled
    ? {
        className: inputClasses,
        id,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
        type: inputType,
      }
    : { className: inputClasses, id, ...refMaybe, ...input, ...rest, type: inputType };

  const labelClassMaybe = labelClassName ? { className: labelClassName } : {};
  const classes = classNames(rootClassName || css.root, className);

  const showPasswordLabel = intl.formatMessage({ id: 'FieldTextInput.showPassword' });
  const hidePasswordLabel = intl.formatMessage({ id: 'FieldTextInput.hidePassword' });
  const passwordToggle = showPasswordToggle ? (
    <button
      type="button"
      className={css.passwordToggle}
      onClick={() => setPasswordVisible(visible => !visible)}
      aria-label={passwordVisible ? hidePasswordLabel : showPasswordLabel}
      aria-pressed={passwordVisible}
    >
      {passwordVisible ? <IconEyeOff /> : <IconEye />}
    </button>
  ) : null;

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} {...labelClassMaybe}>
          {label}
        </label>
      ) : null}
      {isTextarea ? (
        <ExpandingTextarea {...inputProps} />
      ) : showPasswordToggle ? (
        <div className={css.inputWrapper}>
          <input {...inputProps} />
          {passwordToggle}
        </div>
      ) : (
        <input {...inputProps} />
      )}
      <HelpText helpText={helpText} />
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

/**
 * Create Final Form field for <input> or <textarea>.
 * It's often used for type="text" and sometimes with other types like 'number' too.
 * Password inputs get an eye icon to show/hide the password (can be hidden with hidePasswordToggle).
 *
 * Note: Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
 * https://reactjs.org/docs/uncontrolled-components.html#default-values
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.inputRootClass overwrite components own css.input
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id
 * @param {string?} props.label Label is optional, but if it is given, an id is also required.
 * @param {string?} props.customErrorText Error message that can be manually passed to input field, overrides default validation message
 * @param {boolean} props.isUncontrolled is value tracked by parent component
 * @param {Object} props.inputRef a ref object passed for input element.
 * @param {boolean} props.hidePasswordToggle hide the show/hide password eye icon from a type="password" input
 * @param {Function} props.onUnmount Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
 * @returns {JSX.Element} Final Form Field containing nested "select" input
 */
class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
